import React, { useState, useRef } from "react";
import { toast } from "react-hot-toast";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import WebinarService from "../../Services/WebinarService";

export default function AddWebinar({
  show,
  onHide,
  onSuccess,
  pageTitle = "Event/Webinar",
}) {
  const imageRef = useRef(null);
  const [webinarData, setWebinarData] = useState({
    imageFile: null,
    title: "",
    type: "",
    location: "",
    date: "",
    description: "",
  });
  const [validated, setValidated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleImageInput = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPEG, PNG)");
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setWebinarData((prev) => ({
      ...prev,
      imageFile: file,
    }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setWebinarData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setValidated(true);

    if (form.checkValidity() === false) {
      event.stopPropagation();
      return;
    }

    setIsSaving(true);
    try {
      const loadingMessage = toast.loading("Creating...");

      const formData = new FormData();
      if (webinarData.imageFile) {
        formData.append("image", webinarData.imageFile);
      }
      formData.append("title", webinarData.title.trim());
      formData.append("type", webinarData.type.trim());
      formData.append("location", webinarData.location.trim());
      formData.append("date", webinarData.date.trim());
      formData.append("description", webinarData.description.trim());

      const response = await WebinarService.createWebinar(formData);

      if (response.data.success) {
        toast.success(response.data.message, { id: loadingMessage });
        handleClose();
        onSuccess && onSuccess();
      } else {
        toast.error(response.data.message || "Something went wrong", {
          id: loadingMessage,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setValidated(false);
    setWebinarData({
      imageFile: null,
      title: "",
      type: "",
      location: "",
      date: "",
      description: "",
    });
    onHide();
  };

  const handleRemoveNewImage = () => {
    setWebinarData((prev) => ({
      ...prev,
      imageFile: null,
    }));
    // Reset the file input
    if (imageRef.current) {
      imageRef.current.value = "";
    }
  };

  return (
    <Modal show={show} onHide={handleClose} className="mt-5" size="xl">
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>
          <i className="bi bi-calendar-event me-2"></i>
          Add {pageTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          className="px-3"
        >
          <Row xs={12} md={12} className="g-3">
            {/* Title */}
            <Col xs={12} md={6}>
              <Form.Group controlId="title">
                <Form.Label className="form-label fs-5">
                  Event/Webinar Title *
                </Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={webinarData.title}
                  onChange={handleInputChange}
                  placeholder="Enter event or webinar title"
                  required
                  isInvalid={validated && !webinarData.title.trim()}
                  className="form-control fs-5"
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a title
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Type */}
            <Col xs={12} md={6}>
              <Form.Group controlId="type">
                <Form.Label className="form-label fs-5">Type *</Form.Label>
                <Form.Control
                  type="text"
                  name="type"
                  value={webinarData.type}
                  onChange={handleInputChange}
                  placeholder="Enter type (e.g., Webinar, Workshop, etc.)"
                  required
                  isInvalid={validated && !webinarData.type.trim()}
                  className="form-control fs-5"
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a type
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Location */}
            <Col xs={12} md={6}>
              <Form.Group controlId="location">
                <Form.Label className="form-label fs-5">Location *</Form.Label>
                <Form.Control
                  type="text"
                  name="location"
                  value={webinarData.location}
                  onChange={handleInputChange}
                  placeholder="Enter location (e.g., City, Venue)"
                  required
                  isInvalid={validated && !webinarData.location.trim()}
                  className="form-control fs-5"
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a location
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Date */}
            <Col xs={12} md={6}>
              <Form.Group controlId="date">
                <Form.Label className="form-label fs-5">
                  Event Date & Time *
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="date"
                  value={webinarData.date}
                  onChange={handleInputChange}
                  required
                  isInvalid={validated && !webinarData.date.trim()}
                  className="form-control fs-5"
                />
                <Form.Control.Feedback type="invalid">
                  Please select event date and time
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Description */}
            <Col xs={12} md={12}>
              <Form.Group controlId="description">
                <Form.Label className="form-label fs-5">
                  Event Theme *
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  value={webinarData.description}
                  onChange={handleInputChange}
                  placeholder="Enter event or webinar description"
                  required
                  isInvalid={validated && !webinarData.description.trim()}
                  className="form-control fs-5"
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a description
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Image */}
            <Col xs={12} md={12}>
              <Form.Label className="form-label fs-5">Event Image *</Form.Label>

              {/* Show new image preview */}
              {webinarData.imageFile ? (
                <div className="mt-2">
                  <p className="text-muted small mb-2">New Image:</p>
                  <div className="d-flex position-relative">
                    <img
                      src={URL.createObjectURL(webinarData.imageFile)}
                      alt="New Upload"
                      style={{ maxWidth: "90%", maxHeight: "200px" }}
                      className="img-fluid"
                    />
                    <button
                      type="button"
                      className="btn btn-link"
                      style={{
                        position: "absolute",
                        right: "35px",
                        top: "-13px",
                      }}
                      onClick={handleRemoveNewImage}
                    >
                      <i className="bi bi-x-circle-fill text-danger"></i>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <label
                    htmlFor="importInput_image"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-cloud-plus me-2"></i> Upload Image
                  </label>
                  <input
                    id="importInput_image"
                    type="file"
                    className="d-none form-control fs-5"
                    ref={imageRef}
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleImageInput}
                    required
                  />
                  <Form.Control.Feedback type="invalid" className="label fs-5">
                    Please select an image file (JPG, PNG)
                  </Form.Control.Feedback>
                </div>
              )}
            </Col>

            <Modal.Footer className="mt-4">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSaving}
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </Button>
              <Button type="submit" variant="success" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Create Event
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
